import { useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { CloudRain, Layers, Navigation, MapPin, FileText } from "lucide-react";
import { weatherSvg } from "@/lib/weatherIcon";

const directionsUrl = (lat, lng) =>
  `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;

const KAYONG_BOUNDS = [
  [-1.75, 108.85],
  [-0.75, 110.2],
];

const MAP_STYLES = {
  satellite: { label: "Satelit", lyrs: "y", maxZoom: 20 },
  roadmap: { label: "Jalan", lyrs: "m", maxZoom: 20 },
  terrain: { label: "Terrain", lyrs: "p", maxZoom: 18 },
};

const flameSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#0b130e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>`;
const reportSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>`;

const hotspotIcon = (risk, source) => {
  const color =
    risk === "TINGGI" ? "#ef4444" : risk === "WASPADA" ? "#f97316" : "#22c55e";
  const ring =
    risk === "TINGGI"
      ? "rgba(239,68,68,0.3)"
      : risk === "WASPADA"
        ? "rgba(249,115,22,0.3)"
        : "rgba(34,197,94,0.3)";
  return L.divIcon({
    className: "sitako-hotspot-icon",
    html: `<div class="hs-pin" style="background:${color};box-shadow:0 0 0 3px ${ring};">${flameSvg}</div>`,
    iconSize: [17, 17],
    iconAnchor: [9, 9],
    popupAnchor: [0, -9],
  });
};

const districtIcon = (risk, weather, temperature) => {
  const color =
    risk === "TINGGI" ? "#ef4444" : risk === "WASPADA" ? "#f59e0b" : "#22c55e";
  const chip =
    temperature != null
      ? `<span class="d-chip">${weatherSvg(weather)}${Math.round(temperature)}°C</span>`
      : "";
  return L.divIcon({
    className: "sitako-district-icon",
    html: `<div class="d-wrap"><div class="d-pin" style="background:${color}"><i></i></div>${chip}</div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8],
  });
};

const severityColor = (severity) =>
  severity === "TINGGI" ? "#ef4444" : severity === "WASPADA" ? "#f59e0b" : "#1f7a4d";

const reportIcon = (severity) =>
  L.divIcon({
    className: "sitako-report-icon",
    html: `<div class="r-pin" style="background:${severityColor(severity)}">${reportSvg}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });

const clusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const markers = cluster.getAllChildMarkers();
  const hasHigh = markers.some((m) => m.options?.risk === "TINGGI");
  const cls = hasHigh ? "cl-high" : count >= 5 ? "cl-mid" : "cl-low";
  return L.divIcon({
    html: `<div class="cluster-pin ${cls}"><b>${count}</b><span>titik api</span></div>`,
    className: "sitako-cluster-icon",
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
};

function HotspotCluster({ hotspots, onRoute }) {
  const map = useMap();
  const groupRef = useRef(null);

  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 55,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: clusterIcon,
    });
    groupRef.current = group;
    map.addLayer(group);
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.clearLayers();
    hotspots.forEach((h) => {
      const marker = L.marker([h.lat, h.lng], {
        icon: hotspotIcon(h.risk, h.source),
        risk: h.risk,
      });
      marker.on("dblclick", () => onRoute(h.lat, h.lng));
      marker.bindPopup(popupHtml(h), { minWidth: 220, autoPan: true });
      marker.on("popupopen", () => {
        const btn = document.getElementById(`route-${h.id}`);
        if (btn) btn.onclick = () => onRoute(h.lat, h.lng);
      });
      group.addLayer(marker);
    });
  }, [hotspots, onRoute]);

  return null;
}

function popupHtml(h) {
  const sourceLabel =
    h.source === "SIPONGI"
      ? "SiPongi KLHK"
      : h.source === "simulasi"
        ? "SIMULASI"
        : "NASA FIRMS";
  const rows = [
    ["Kecamatan", h.district],
    h.desa && ["Desa", h.desa],
    ["Koordinat", `${h.lat}, ${h.lng}`],
    ["Confidence", `${h.confidence}%`],
    h.satellite && ["Satelit", h.satellite],
    h.acq_date && ["Waktu", `${h.acq_date} ${h.acq_time || ""}`],
    h.frp != null && ["FRP", `${h.frp} MW`],
    [
      "Kategori",
      h.category ||
        (h.risk === "TINGGI"
          ? "Tinggi"
          : h.risk === "WASPADA"
            ? "Sedang"
            : "Rendah"),
    ],
  ].filter(Boolean);
  const rowHtml = rows
    .map(
      ([k, v]) =>
        `<span><span>${k}</span><em class="s-${(h.risk || "").toLowerCase()}">${v}</em></span>`,
    )
    .join("");
  return `<div class="iw" data-testid="iw-hotspot">
      <span class="iw-kicker">HOTSPOT · ${sourceLabel}</span>
      <b>${h.id}</b>
      <div class="iw-rows">${rowHtml}</div>
      <button type="button" id="route-${h.id}" class="iw-route" data-testid="iw-route-hotspot">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"></polygon></svg>
        Buka rute ke titik api
      </button>
    </div>`;
}

const ctxClusterIcon = (cluster) => {
  const children = cluster.getAllChildMarkers();
  let cls = "cl-grn";
  for (const m of children) {
    const c = m.options.category;
    if (c === "Tinggi") {
      cls = "cl-high";
      break;
    }
    if (c === "Sedang") cls = "cl-mid";
  }
  return L.divIcon({
    className: "sitako-hotspot-icon",
    html: `<div class="cluster-pin ${cls}"><b>${cluster.getChildCount()}</b><span>LUAR KKU</span></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
};

const ctxIcon = (category) => {
  const color =
    category === "Tinggi" ? "#ef4444" : category === "Sedang" ? "#f97316" : "#22c55e";
  return L.divIcon({
    className: "sitako-hotspot-icon",
    html: `<div class="ctx-pin" style="background:${color}"></div>`,
    iconSize: [11, 11],
    iconAnchor: [5, 5],
    popupAnchor: [0, -5],
  });
};

function KalbarCluster({ hotspots }) {
  const map = useMap();
  const groupRef = useRef(null);

  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      chunkedLoading: true,
      iconCreateFunction: ctxClusterIcon,
    });
    groupRef.current = group;
    map.addLayer(group);
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.clearLayers();
    hotspots.forEach((h) => {
      const marker = L.marker([h.lat, h.lng], {
        icon: ctxIcon(h.category),
        category: h.category,
      });
      marker.bindPopup(
        `<div class="iw"><span class="iw-kicker">HOTSPOT · LUAR KAYONG UTARA</span>
        <b>${h.kabupaten || h.provinsi || "Indonesia"}</b>
        <div class="iw-rows">
          ${h.kecamatan ? `<span><span>Kecamatan</span><em>${h.kecamatan}</em></span>` : ""}
          ${h.provinsi ? `<span><span>Provinsi</span><em>${h.provinsi}</em></span>` : ""}
          <span><span>Koordinat</span><em>${h.lat}, ${h.lng}</em></span>
          <span><span>Kategori</span><em>${h.category}</em></span>
          ${h.acq_date ? `<span><span>Waktu</span><em>${h.acq_date} ${h.acq_time || ""}</em></span>` : ""}
          <span><span>Sumber</span><em>${h.source === "SIPONGI" ? "SiPongi KLHK" : "NASA FIRMS"}</em></span>
        </div></div>`,
        { minWidth: 200 },
      );
      group.addLayer(marker);
    });
  }, [hotspots]);

  return null;
}

function FitOnChange({ selectedDistrict, districts }) {
  const map = useMap();
  useEffect(() => {
    if (selectedDistrict === "Semua wilayah") {
      map.fitBounds(KAYONG_BOUNDS, { padding: [20, 20] });
      return;
    }
    const target = districts.find((d) => d.name === selectedDistrict);
    if (target) {
      map.setView([target.lat, target.lng], 11, { animate: true });
    }
  }, [selectedDistrict, districts, map]);
  return null;
}

function useRainRadar() {
  const [radar, setRadar] = useState({ host: "", frames: [] });
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetch("https://api.rainviewer.com/public/weather-maps.json")
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          const past = (data?.radar?.past || []).slice(-7);
          const nowcast = (data?.radar?.nowcast || []).map((f) => ({
            ...f,
            forecast: true,
          }));
          const frames = [...past, ...nowcast];
          if (frames.length) setRadar({ host: data.host, frames });
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);
  return radar;
}

function RadarAnimation({ host, frames, playing }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [frames]);

  useEffect(() => {
    if (!playing || frames.length < 2) return undefined;
    const t = setInterval(
      () => setIdx((i) => (i + 1) % frames.length),
      // jeda lebih lama di frame terakhir agar arah pergerakan terbaca
      750,
    );
    return () => clearInterval(t);
  }, [playing, frames.length]);

  if (!host || !frames.length) return null;
  const active = playing ? idx : frames.length - 1;
  return frames.map((f, i) => (
    <TileLayer
      key={f.path}
      url={`${host}${f.path}/256/{z}/{x}/{y}/2/1_1.png`}
      opacity={i === active ? 0.6 : 0}
      maxNativeZoom={7}
      minNativeZoom={0}
      maxZoom={20}
      zIndex={210 + i}
      attribution='Radar hujan &copy; <a href="https://www.rainviewer.com">RainViewer</a>'
    />
  ));
}

export default function MapPanel({ hotspots, kalbarHotspots = [], districts, reports = [], selectedDistrict }) {
  const [showRadar, setShowRadar] = useState(true);
  const [showReports, setShowReports] = useState(true);
  const [mapStyle, setMapStyle] = useState("satellite");
  const radar = useRainRadar();
  const visibleHotspots = useMemo(
    () =>
      selectedDistrict === "Semua wilayah"
        ? hotspots
        : hotspots.filter((h) => h.district === selectedDistrict),
    [hotspots, selectedDistrict],
  );
  const districtMarkers = useMemo(
    () =>
      selectedDistrict === "Semua wilayah"
        ? districts
        : districts.filter((d) => d.name === selectedDistrict),
    [districts, selectedDistrict],
  );
  const visibleReports = useMemo(
    () =>
      reports.filter(
        (r) =>
          r.lat != null &&
          r.lng != null &&
          (selectedDistrict === "Semua wilayah" || r.district === selectedDistrict),
      ),
    [reports, selectedDistrict],
  );

  const openRoute = (lat, lng) => {
    window.open(directionsUrl(lat, lng), "_blank", "noopener");
  };

  const style = MAP_STYLES[mapStyle];
  const lastPast = radar.frames.filter((f) => !f.forecast).slice(-1)[0];
  const radarLabel = lastPast
    ? new Date(lastPast.time * 1000).toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "…";

  return (
    <div className="map-shell" data-testid="monitoring-map">
      <MapContainer
        bounds={KAYONG_BOUNDS}
        boundsOptions={{ padding: [20, 20] }}
        scrollWheelZoom
        style={{ height: "100%", width: "100%", background: "#0b130e" }}
      >
        <TileLayer
          key={mapStyle}
          attribution='Peta &copy; Google'
          url={`https://mt{s}.google.com/vt/lyrs=${style.lyrs}&x={x}&y={y}&z={z}&hl=id`}
          subdomains={["0", "1", "2", "3"]}
          maxZoom={style.maxZoom}
        />
        {showRadar && (
          <RadarAnimation
            host={radar.host}
            frames={radar.frames}
            playing={showRadar}
          />
        )}
        <FitOnChange
          selectedDistrict={selectedDistrict}
          districts={districts}
        />
        {districtMarkers.map((d) => (
          <Marker
            key={`d-${d.name}`}
            position={[d.lat, d.lng]}
            icon={districtIcon(d.overall_risk, d.weather, d.temperature)}
          >
            <Popup>
              <DistrictPopup d={d} onRoute={openRoute} />
            </Popup>
          </Marker>
        ))}
        <HotspotCluster hotspots={visibleHotspots} onRoute={openRoute} />
        {kalbarHotspots.length > 0 && (
          <KalbarCluster hotspots={kalbarHotspots} />
        )}
        {showReports &&
          visibleReports.map((r) => (
            <Marker
              key={`r-${r.id}`}
              position={[r.lat, r.lng]}
              icon={reportIcon(r.severity)}
            >
              <Popup>
                <ReportPopup r={r} onRoute={openRoute} />
              </Popup>
            </Marker>
          ))}
      </MapContainer>

      {visibleHotspots.length === 0 && (
        <div className="map-empty-notice" data-testid="map-empty-notice">
          <b>Tidak ada titik panas terdeteksi di Kayong Utara pada periode ini.</b>
          <span>
            Satelit NASA/SiPongi melintas ±pukul 01.30 &amp; 13.30 WIB — data akan muncul
            otomatis setelah lintasan berikutnya. Coba periode <i>"24 jam terakhir"</i> atau{" "}
            <i>"Kemarin"</i> untuk melihat deteksi sebelumnya.
          </span>
        </div>
      )}

      <div className="map-legend" data-testid="map-legend">
        <span>
          <i className="dot" style={{ background: "#22c55e" }} /> Kepercayaan rendah (
          {visibleHotspots.filter((h) => h.risk === "RENDAH").length})
        </span>
        <span>
          <i className="dot" style={{ background: "#f97316" }} /> Sedang (
          {visibleHotspots.filter((h) => h.risk === "WASPADA").length})
        </span>
        <span>
          <i className="dot" style={{ background: "#ef4444" }} /> Tinggi (
          {visibleHotspots.filter((h) => h.risk === "TINGGI").length})
        </span>
        <span>
          <i className="dot rain-dot" /> Wilayah ({districtMarkers.length})
        </span>
        <span>
          <i className="dot report-dot" /> Laporan ({visibleReports.length})
        </span>
        <span>
          <i className="dot" style={{ background: "#f97316" }} /> Luar Kayong Utara ({kalbarHotspots.length}) · warna sesuai kepercayaan
        </span>
      </div>

      <div className="map-controls">
        <div className="map-style-group" data-testid="map-style-group">
          <Layers size={12} />
          {Object.entries(MAP_STYLES).map(([key, cfg]) => (
            <button
              key={key}
              type="button"
              className={mapStyle === key ? "active" : ""}
              onClick={() => setMapStyle(key)}
              data-testid={`map-style-${key}`}
            >
              {cfg.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`radar-toggle ${showRadar ? "on" : "off"}`}
          onClick={() => setShowRadar((v) => !v)}
          data-testid="radar-toggle"
          title="Aktifkan/matikan lapisan radar hujan"
        >
          <CloudRain size={13} />
          {showRadar ? `Radar animasi · ${radarLabel} WIB` : "Radar mati"}
        </button>
        <button
          type="button"
          className={`radar-toggle ${showReports ? "on rp" : "off"}`}
          onClick={() => setShowReports((v) => !v)}
          data-testid="reports-toggle"
          title="Tampilkan/sembunyikan pin laporan kejadian"
        >
          <FileText size={13} />
          {showReports ? `Laporan · ${visibleReports.length}` : "Laporan mati"}
        </button>
      </div>

      <div className="map-note">
        <MapPin size={14} /> Klik pin api · dobel-klik untuk buka rute
      </div>
    </div>
  );
}

function ReportPopup({ r, onRoute }) {
  return (
    <div className="iw" data-testid="iw-report">
      <span className="iw-kicker">
        LAPORAN · {r.incident_type?.replace("_", " ")}
      </span>
      <b>{r.title}</b>
      <div className="iw-rows">
        <span>
          <span>Wilayah</span>
          <em>Kec. {r.district}</em>
        </span>
        {r.location_detail && (
          <span>
            <span>Lokasi</span>
            <em>{r.location_detail}</em>
          </span>
        )}
        <span>
          <span>Waktu</span>
          <em>{new Date(r.happened_at).toLocaleString("id-ID")}</em>
        </span>
        <span>
          <span>Tingkat</span>
          <em className={`s-${r.severity?.toLowerCase()}`}>{r.severity}</em>
        </span>
      </div>
      <div className="iw-actions">
        <a
          href={`/laporan/${r.public_slug}`}
          className="iw-route iw-secondary"
          target="_blank"
          rel="noreferrer"
        >
          <FileText size={12} /> Buka laporan
        </a>
        <button
          type="button"
          onClick={() => onRoute(r.lat, r.lng)}
          className="iw-route"
        >
          <Navigation size={12} /> Buka rute
        </button>
      </div>
    </div>
  );
}

function DistrictPopup({ d, onRoute }) {
  return (
    <div className="iw" data-testid="iw-district">
      <span className="iw-kicker">KECAMATAN</span>
      <b>{d.name}</b>
      <div className="iw-rows">
        <span>
          <span>Cuaca kini</span>
          <em>{d.weather || "—"}</em>
        </span>
        <span>
          <span>Suhu udara</span>
          <em>{d.temperature != null ? `${d.temperature}°C` : "—"}</em>
        </span>
        <span>
          <span>Kelembapan</span>
          <em>{d.humidity != null ? `${d.humidity}%` : "—"}</em>
        </span>
        <span>
          <span>ISPU</span>
          <em style={d.ispu_color ? { color: d.ispu_color === "#111827" ? "#94a3b8" : d.ispu_color } : undefined}>
            {d.ispu != null ? `${d.ispu} · ${d.ispu_category}` : "—"}
          </em>
        </span>
        <span>
          <span>Hujan saat ini</span>
          <em>{d.rain_now != null ? `${d.rain_now} mm/3j` : "—"}</em>
        </span>
        <span>
          <span>Prakiraan hujan 24 jam</span>
          <em>{d.rainfall != null ? `${d.rainfall} mm` : "—"}</em>
        </span>
        <span>
          <span>Gelombang</span>
          <em>{d.wave_height != null ? `${d.wave_height} m` : "—"}</em>
        </span>
        <span>
          <span>Hotspot</span>
          <em>{d.hotspot_count} titik</em>
        </span>
        <span>
          <span>Status</span>
          <em className={`s-${d.overall_risk?.toLowerCase()}`}>
            {d.overall_risk}
          </em>
        </span>
      </div>
      <button
        type="button"
        onClick={() => onRoute(d.lat, d.lng)}
        className="iw-route"
        data-testid="iw-route-district"
      >
        <Navigation size={12} /> Buka rute
      </button>
    </div>
  );
}
